<script setup lang="ts">
import { type AcceptableValue, RadioGroupItem, RadioGroupRoot } from 'reka-ui';
import { computed, ref, useSlots } from 'vue';

import { KaDescription, KaMessage } from '@/kasten-components/components';
import type { KaVeeFormFieldWithoutInputEmitContract } from '@/kasten-components/components/KaVeeFormField';
import { useDataTestid } from '@/kasten-components/composables';
import type { HTMLElementEvent } from '@/kasten-components/types';
import { unwrapElement } from '@/kasten-components/utils';
import { cnMerge } from '@/utils/utils.js';

import type { KaRadioGroupOptionValue, KaRadioGroupProps } from './types';
export type { KaRadioGroupOption, KaRadioGroupOptionValue, KaRadioGroupProps } from './types';

const props = withDefaults(defineProps<KaRadioGroupProps>(), {
  // By default, vue sets undefined values to false, without this line, the tests would fail
  modelValue: undefined,
  hideLabel: false,
  hideDescription: false,
  mode: 'lazy',
  showErrorStylesOnly: false,
});

// prettier-ignore
const emit = defineEmits< KaVeeFormFieldWithoutInputEmitContract<HTMLElementEvent<HTMLInputElement>> & { (e: 'blur', event: FocusEvent): void; (e: 'touch'): void; (e: 'update:modelValue', value: KaRadioGroupOptionValue): void; (e: 'change', value: KaRadioGroupOptionValue): void; } >();

const slots = useSlots();

const fieldSetRef = ref<HTMLFieldSetElement>();

const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('touch');
    emit('update:modelValue', value!);
    emit('change', value!);
  },
});

const isRequired = computed(() => props.required || props.ariaRequired);

const id = computed(() => props.id ?? props.name);
const { dataTestid, suffix } = useDataTestid(computed(() => props.dataTestid ?? id.value));
const labelId = computed(() => `${id.value}-label`);
const descriptionId = computed(() =>
  props.description || slots.description || isRequired.value
    ? `${id.value}-description`
    : undefined,
);
const fallbackErrorMessageId = computed(() => `${id.value}-error-message`);
const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);

const hasError = computed(() => props.errorMessage !== undefined);

const handleBlur = (event: FocusEvent) =>
  setTimeout(() => {
    if (shouldDispatchBlur(event)) {
      emit('blur', event);
    }
  });

// Decides whether blur event should be dispatched or not
const shouldDispatchBlur = (event: FocusEvent) => {
  const nextTarget = event.relatedTarget ?? document.activeElement;
  // Blur didn't result in focus on smth else
  if (nextTarget === null) return true;

  // If target is not an HTML Node - we can't react on it anyhow. Consider that this is blur.
  if (!(nextTarget instanceof Node)) return true;

  const fieldsetEl = unwrapElement(fieldSetRef);
  if (!fieldsetEl) return true;

  return !fieldsetEl.contains(nextTarget);
};

const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId.value : undefined;
});

// Combine description and error ids so SRs announce both on group and item focus
const describedByIds = computed<string | undefined>(() => {
  const ids = [descriptionId.value, computedErrorMessageId.value].filter((v): v is string =>
    Boolean(v),
  );
  return ids.length ? ids.join(' ') : undefined;
});
</script>

<template>
  <RadioGroupRoot
    v-model="internalValue as unknown as AcceptableValue | undefined"
    as-child
    :orientation="props.inline ? 'horizontal' : 'vertical'"
  >
    <fieldset
      :id="id"
      ref="fieldSetRef"
      :data-testid="dataTestid"
      role="radiogroup"
      :aria-labelledby="labelId"
      :aria-describedby="describedByIds"
      :aria-invalid="props.showErrorStylesOnly || hasError"
      :aria-errormessage="computedErrorMessageId"
      :aria-orientation="props.inline ? 'horizontal' : 'vertical'"
    >
      <legend
        :id="labelId"
        :data-testid="suffix('label')"
        :class="
          cnMerge(
            'text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50',
            !shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0',
            { 'sr-only': props.hideLabel },
          )
        "
      >
        <slot name="label" v-bind="{ label: props.label }">{{ props.label }}</slot>
        <span v-if="isRequired && !props.hideRequiredAsterisk">&nbsp;*</span>
      </legend>

      <KaDescription
        v-if="props.description || $slots.description || isRequired"
        :id="descriptionId"
        :data-testid="suffix('description')"
        :hide-description="
          props.hideDescription || (!props.description && !$slots.description && isRequired)
        "
        :class="shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
      >
        <slot name="description" v-bind="{ description: props.description }">
          {{ props.description }}
        </slot>

        <span v-if="isRequired" class="sr-only">
          {{ props.label || 'This' }} is a required field.
        </span>
      </KaDescription>

      <div :class="cnMerge('flex flex-col gap-x-10 gap-y-4', { 'sm:flex-row': props.inline })">
        <div
          v-for="(opt, idx) in props.options"
          :key="opt.id ?? idx"
          :class="cnMerge('flex', opt.description ? 'items-start' : 'items-center')"
        >
          <RadioGroupItem as-child :value="opt.value as any" :disabled="opt.disabled">
            <input
              :id="opt.id ?? `${id}-radio-option-${idx}`"
              v-model="internalValue"
              :data-testid="suffix(`radio-option-${opt.label}`)"
              :value="opt.value"
              :name="props.name"
              :disabled="opt.disabled"
              type="radio"
              :checked="internalValue === opt.value"
              :aria-invalid="props.showErrorStylesOnly || hasError"
              :aria-errormessage="computedErrorMessageId"
              :aria-labelledby="`${opt.id ?? `${id}-radio-option-${idx}`}-label`"
              :aria-describedby="
                [
                  describedByIds,
                  opt.description ? `${opt.id ?? `${id}-radio-option-${idx}`}-description` : null,
                ]
                  .filter(Boolean)
                  .join(' ') || undefined
              "
              :class="
                cnMerge(
                  'form-radio size-4 border-gray-300 bg-gray-200 text-blue-500 checked:border-current checked:bg-current focus:ring-blue-500 dark:border-white/20 dark:bg-white/10 dark:checked:border-current dark:checked:bg-current dark:focus:ring-offset-navy-900',
                  opt.description ? 'mt-1' : '',
                  opt.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                )
              "
              @blur="handleBlur"
            />
          </RadioGroupItem>

          <span
            v-if="opt.left"
            class="ml-3 flex size-5 flex-shrink-0 flex-grow-0 items-center justify-center [&>svg]:block [&>svg]:align-baseline"
            aria-hidden="true"
          >
            <component
              :is="opt.left.icon"
              v-if="opt.left"
              :class="cnMerge('size-3.5', opt.left.classes)"
            />
          </span>

          <label
            :for="opt.id ?? `${id}-radio-option-${idx}`"
            :class="cnMerge('block text-sm leading-6', opt.left ? 'ml-1.5' : 'ml-3')"
          >
            <span
              :id="`${opt.id ?? `${id}-radio-option-${idx}`}-label`"
              class="font-semibold text-gray-900 dark:text-gray-50"
            >
              {{ opt.label }}
            </span>
            <span
              v-if="opt.description"
              :id="`${opt.id ?? `${id}-radio-option-${idx}`}-description`"
              class="block text-sm font-normal leading-5 text-gray-600 dark:text-gray-400"
            >
              {{ opt.description }}
            </span>
          </label>
        </div>
      </div>

      <KaMessage
        v-if="!props.showErrorStylesOnly && hasError"
        :id="fallbackErrorMessageId"
        :data-testid="suffix('error-message')"
        :message="props.errorMessage"
        background="transparent"
        variant="error"
        :role="null"
        :class="['mt-2', { 'sr-only': props.hideErrorMessage }]"
      />
    </fieldset>
  </RadioGroupRoot>
</template>
